import type { Control, FieldValues, Path } from "react-hook-form";

/**
 * Represents a time value with hour (0-24) and minute (0-59)
 */
export interface TimeValue {
  readonly hour: number;
  readonly minute: number;
}

/**
 * Common props for all picker field components
 */
export interface PickerFieldProps<TValue> {
  value?: TValue;
  onChange: (value: TValue) => void;
  error?: string;
  size?: "small" | "medium" | "large";
}

/**
 * Props for date picker field
 */
export interface DatePickerFieldProps extends PickerFieldProps<string> {
  defaultDate?: string;
  minimumDate?: Date;
  maximumDate?: Date;
}

/**
 * Props for time picker field
 */
export interface TimePickerFieldProps extends PickerFieldProps<TimeValue> {
  is24Hour?: boolean;
  minimumDate?: Date;
  maximumDate?: Date;
}

/**
 * Props for react-hook-form controlled picker
 */
export interface PickerControllerProps<T extends FieldValues, TValue> {
  control: Control<T>;
  name: Path<T>;
  error?: string;
  size?: "small" | "medium" | "large";
}

/**
 * Props for date controller (react-hook-form)
 */
export interface DateControllerProps<T extends FieldValues> extends PickerControllerProps<T, string> {
  defaultDate?: string;
  minimumDate?: Date;
  maximumDate?: Date;
}

/**
 * Props for time controller (react-hook-form)
 */
export interface TimeControllerProps<T extends FieldValues> extends PickerControllerProps<T, TimeValue> {
  is24Hour?: boolean;
  minimumDate?: Date;
  maximumDate?: Date;
}

/**
 * Formatter function type for PickerInput
 */
export type PickerFormatter<TValue> = (value: TValue | undefined) => string;

/**
 * Icon type for PickerInput
 */
export type PickerIcon = "calendar" | "time";

/**
 * Constants for picker modal styling
 */
export const PICKER_CONSTANTS = {
  MODAL_PADDING_MULTIPLIER: 0.4,
  MODAL_PADDING_OFFSET: 150,
  IOS_PICKER_HEIGHT: 220,
  MODAL_OPACITY: 0.5,
  MODAL_MAX_WIDTH: 400,
  MODAL_WIDTH_PERCENT: "90%",
} as const;

/**
 * Default date range constraints
 */
export const DATE_CONSTRAINTS = {
  MIN_DATE: new Date(2000, 0, 1),
  MAX_DATE: new Date(2030, 0, 1),
} as const;
